import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { useEdmDocumentationAccessesCountsQuery } from 'graphql-common';
import { TabBarItem as TabBarItemType } from '@lib/components/TabBar/constants';
import TabBar from '@lib/components/TabBar/TabBar';
import getAccessesTabs from './utils/getAccessesTabs';
import SectorsTab from './SectorsTab';
import SitesTab from './SitesTab';

const TABS = {
  sectors: 'sectors',
  sites: 'sites',
};

interface DocumentViewAccessesProps {
  documentId: string;
  isSitesAvailable: boolean;
  isViewLocked?: boolean;
}

export default function DocumentViewAccesses({
  documentId,
  isSitesAvailable,
  isViewLocked,
}: DocumentViewAccessesProps) {
  const { t } = useTranslation();

  const { data: countsData, refetch: refetchCounts } =
    useEdmDocumentationAccessesCountsQuery({
      variables: { documentationId: documentId },
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'ignore',
      skip: !documentId || !isSitesAvailable || isViewLocked,
    });

  const tabs = useMemo(
    () =>
      getAccessesTabs({
        t,
        isSitesAvailable,
        sectorsCount: _get(countsData, 'sectors.metadata.totalCount', 0),
        sitesCount: _get(countsData, 'sites.metadata.totalCount', 0),
      }),
    [countsData, isSitesAvailable, t],
  );

  const [activeSubTab, setActiveSubTab] = useState(
    () => tabs[0]?.value ?? TABS.sectors,
  );

  const onActiveChange = useCallback(
    ({ value }: TabBarItemType) => {
      if (value === activeSubTab) return;
      setActiveSubTab(value);
      refetchCounts();
    },
    [activeSubTab, refetchCounts],
  );

  if (tabs.length === 0) return null;

  return (
    <>
      <TabBar
        items={tabs}
        active={activeSubTab}
        onActiveChange={onActiveChange}
      />
      {activeSubTab === TABS.sectors && <SectorsTab documentId={documentId} />}
      {activeSubTab === TABS.sites && <SitesTab documentId={documentId} />}
    </>
  );
}
